// TODO: switch to @wandb/weave/components/RadixTooltip once it exposes Arrow.
import * as Tooltip from '@radix-ui/react-tooltip';
import {Icon, IconButton} from '@wandb/components';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import React, {useEffect, useState} from 'react';
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';

import type {UnseenSettledThread} from '../../agentChat/threadReadStatus';
import {isMountedInShell} from '../../shellRemote';
import {AGENT_NAME} from './constants';

export const WBAgentChatButton = ({
  disableTooltip,
  isLoading,
  showIntroTooltip,
  hideIntroTooltip,
  unseenSettledThread,
  onImpression,
  onPress,
  ...props
}: AriaButtonProps & {
  disableTooltip?: boolean;
  isLoading?: boolean;
  showIntroTooltip?: boolean;
  hideIntroTooltip: () => void;
  unseenSettledThread: UnseenSettledThread | null;
  onImpression?: () => void;
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isAriaShellHosted = isMountedInShell();

  const dismissTooltip = () => {
    hideIntroTooltip();
    setIsTooltipOpen(false);
  };
  const handlePress: AriaButtonProps['onPress'] = event => {
    dismissTooltip();
    onPress?.(event);
  };

  // Fire once on mount — the button only renders when visible (i.e. the agent
  // is enabled for the user).
  useEffect(() => {
    onImpression?.();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const button = (
    <AriaButton
      aria-label={isLoading ? `${AGENT_NAME} (loading)` : AGENT_NAME}
      {...props}
      onPress={handlePress}
      className="group relative rounded-full p-4 mr-8">
      {showIntroTooltip && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-4 rounded-[5px] bg-[color-mix(in_srgb,var(--teal-250)_50%,transparent)] blur-[8px] dark:blur-[8px] motion-safe:animate-pulse motion-reduce:opacity-60"
        />
      )}
      <div
        className={clsx(
          'relative inline-flex h-32 pl-12 pr-14 items-center gap-8 rounded-[5px] text-base font-semibold whitespace-nowrap outline outline-2 outline-transparent group-focus-visible:outline-[var(--teal-500)] group-focus-visible:rounded-[9px] group-focus-visible:outline-offset-2 group-aria-[expanded=true]:border-[var(--teal-500)] group-aria-[expanded=true]:bg-[color-mix(in_srgb,var(--teal-500)_14%,transparent)]',
          isAriaShellHosted
            ? 'border border-wb-border bg-transparent text-wb-text-primary group-hover:border-wb-border-strong group-hover:bg-moon-100 dark:group-hover:bg-wb-surface-sunken'
            : 'border border-moon-550 bg-white/[0.06] text-white group-hover:border-moon-450 group-hover:bg-white/[0.11]',
          "font-['Source_Sans_3',sans-serif]"
        )}>
        <span
          className={
            isLoading
              ? 'inline-flex animate-spin [animation-duration:4s]'
              : 'inline-flex'
          }>
          <Icon
            icon={isLoading ? 'loading' : 'lightboard-solid'}
            size="md"
            color={
              isLoading
                ? isAriaShellHosted
                  ? 'moon-500'
                  : 'moon-50'
                : 'teal-500'
            }
          />
        </span>
        Ask {AGENT_NAME}
      </div>
      {unseenSettledThread != null && (
        <div
          role="img"
          aria-label="Unread notice"
          className="absolute -right-2 -top-5">
          <Icon icon="circle-filled" size="xs" color="red-500" />
        </div>
      )}
    </AriaButton>
  );

  return (
    <Tailwind>
      {disableTooltip ? (
        button
      ) : (
        <Tooltip.Provider delayDuration={0}>
          <Tooltip.Root
            open={showIntroTooltip || isTooltipOpen}
            onOpenChange={setIsTooltipOpen}>
            <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
            <Tooltip.Portal>
              <TooltipContent
                side="bottom"
                sideOffset={8}
                className="relative z-[999999] max-w-[300px] rounded text-sm leading-[140%] shadow-md bg-wb-surface-inverse p-12 text-moon-200 dark:text-wb-text-inverse">
                <div>
                  <div className="font-semibold text-wb-text-inverse">
                    {AGENT_NAME}: a coding agent for ML experiments
                  </div>
                  {showIntroTooltip && (
                    <div className="absolute right-8 top-8">
                      <IconButton
                        aria-label={`Dismiss ${AGENT_NAME} tooltip`}
                        icon="close"
                        size="sm"
                        variant="ghost"
                        onPress={dismissTooltip}
                      />
                    </div>
                  )}
                </div>
                <div className="mt-4 text-[#C7CCD1] dark:text-moon-650">
                  Analyze and visualize runs, write code, launch experiments, or
                  ask any W&B question
                </div>
                <Tooltip.Arrow className="fill-wb-surface-inverse dark:fill-moon-50" />
              </TooltipContent>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      )}
    </Tailwind>
  );
};

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof Tooltip.Content>,
  React.ComponentPropsWithoutRef<typeof Tooltip.Content>
>((props, ref) => (
  <TailwindContents>
    <Tooltip.Content ref={ref} {...props} />
  </TailwindContents>
));
